import { action, computed, observable } from 'mobx';

import { AppManager, Input, Variables } from 'core/index';

import { ShipmentQuoteApp, ShipmentQuoteModel } from 'apps/shipmentQuote/app';
import { RFQApp, RFQModel } from 'apps/rfq/app';
import { routerStore } from 'core/routing';
import Alert from 'react-s-alert';
import { stripeStore } from 'apps/payment/stripe';
import { ensureIsGraphqlId } from 'utils/dgraphId';
import { clientStore } from 'apps/client/app';
import { analytics } from 'core/analytics';

import { toLocalStorage } from 'core/localStorage';
import {
  CreateCheckoutMutation,
  ShipmentQuoteQuery,
  IntermediatorShipmentQuoteQuery,
  DeleteShipmentQuoteMutation,
} from './gql';

type QuoteQueryT = IntermediatorShipmentQuoteQuery | ShipmentQuoteQuery;

export default class QuotePageAppManager extends AppManager {
  @observable quote: ShipmentQuoteApp = null;

  @observable quoteId: string = null;

  @observable isCheckoutInitiated = false;

  createCheckoutMutation: CreateCheckoutMutation = null;

  checkoutInput: any = null;

  @observable quoteQuery: QuoteQueryT = null;

  constructor(parent?: AppManager) {
    super(parent);

    this.AppRef('quote', ShipmentQuoteApp);

    this.quoteId = ensureIsGraphqlId(
      routerStore.route.params.quoteId,
      'ShipmentQuote',
    );
    this.variables = new Variables({
      id: () => this.quoteId,
    });
    this.variables.onChange(['id'], this.QueryShipmentQuote);

    this.checkoutInput = new Input({
      createCheckoutInput: () => ({
        shipmentQuoteIds: [this.quote.id],
      }),
    });
  }

  @computed get wasInitialQueryLaunched() {
    return Boolean(this.quoteQuery);
  }

  @computed get loading() {
    return !this.wasInitialQueryLaunched || this.quoteQuery?.loading;
  }

  @computed get notFound() {
    return !this.loading && !this.quote;
  }

  @action QueryShipmentQuote = () => {
    const isIntermediator = clientStore.hasRole('INTERMEDIATOR');
    const Query = isIntermediator
      ? IntermediatorShipmentQuoteQuery
      : ShipmentQuoteQuery;
    this.quoteQuery = new Query(this, {});
    this.quoteQuery.process(
      (data: any) => ShipmentQuoteModel.Load(data),
      this.UpdateQuote,
    );
    this.quoteQuery.query();
  };

  @action UpdateQuote = (quoteModel: ShipmentQuoteModel) => {
    if (!quoteModel) return;
    this.quote = quoteModel;
  };

  @action RedirectToStripe = (result: string) => {
    this.isCheckoutInitiated = false;

    if (!stripeStore.stripe) {
      Alert.error(
        'Stripe payment gateway is not initialized. Please try again.',
      );
      return;
    }

    toLocalStorage('payingRfq', { quoteId: this.quoteId }, true);

    stripeStore.stripe.redirectToCheckout({ sessionId: result });
  };

  @action DeleteShipmentQuote = () => {
    const input = new Input({
      input: { id: this.quoteId },
    });
    const deleteMutation = new DeleteShipmentQuoteMutation(
      input,
      null,
      {},
      true,
    );

    const onDeleteSuccess = () => {
      routerStore.navigate('workspace.intermediator.quotes', {
        spacePath: clientStore.activeSpace?.path,
      });
    };

    const onDeleteError = () => {
      Alert.error('Sorry, there was an error deleting this quote.');
    };

    deleteMutation.mutate({
      onSuccess: onDeleteSuccess,
      onUserErr: onDeleteError,
      onSysErr: onDeleteError,
    });
  };

  @action onPayError = (error: Error) => {
    this.isCheckoutInitiated = false;
    if (error.message === 'shipment quote already has an order') {
      Alert.error('Quote has already been ordered.');
    } else {
      Alert.error('Could not connect to Stripe, please try again...');
    }
  };

  @action PayQuote = () => {
    if (this.quote.productQuotes.length > 1) {
      analytics.sendEvent('multi-part-click-pay-quote');
    } else {
      analytics.sendEvent('single-part-click-pay-quote');
    }

    this.createCheckoutMutation = new CreateCheckoutMutation(
      this.checkoutInput,
      null,
      {
        onSuccess: this.RedirectToStripe,
        onSysErr: this.onPayError,
        onUserErr: this.onPayError,
      },
      true,
    );

    this.isCheckoutInitiated = true;
    this.createCheckoutMutation.mutate();
  };
}
